import { useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "@clerk/react";
import { useAuthFetch } from "./use-auth-fetch";

const STORAGE_KEY = "pa_recent_feeds";
const CHANNEL_NAME = "pa_recent_feeds_sync";
const MAX_FEEDS = 10;
const POLL_INTERVAL_MS = 30_000;

export function readRecentFeeds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((x) => typeof x === "string");
    return [];
  } catch {
    return [];
  }
}

function writeToStorage(feeds: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(feeds));
  } catch {
    // non-fatal
  }
}

export function useRecentFeeds() {
  const { isSignedIn } = useAuth();
  const authFetch = useAuthFetch();
  const [recentFeeds, setRecentFeeds] = useState<string[]>(() => readRecentFeeds());
  const initializedRef = useRef(false);
  const channelRef = useRef<BroadcastChannel | null>(null);

  // Mirror current state in a ref so async callbacks always see the latest value,
  // including any optimistic adds that happened while a fetch was in flight.
  const recentFeedsRef = useRef<string[]>(recentFeeds);
  useEffect(() => {
    recentFeedsRef.current = recentFeeds;
  });

  // Snapshot of localStorage captured at the moment sign-in is first detected,
  // so we can merge pre-signin local history with the server list.
  const localSnapshotRef = useRef<string[]>([]);

  // Set up cross-tab synchronization via BroadcastChannel (with storage event fallback).
  useEffect(() => {
    const useBroadcastChannel = typeof BroadcastChannel !== "undefined";

    if (useBroadcastChannel) {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channelRef.current = channel;
      channel.onmessage = (event: MessageEvent) => {
        if (Array.isArray(event.data)) {
          const feeds = (event.data as unknown[]).filter(
            (x): x is string => typeof x === "string",
          );
          setRecentFeeds(feeds);
        }
      };
      return () => {
        channel.close();
        channelRef.current = null;
      };
    } else {
      // Fallback: the storage event fires in other tabs when localStorage changes.
      const handleStorage = (event: StorageEvent) => {
        if (event.key === STORAGE_KEY && event.newValue !== null) {
          try {
            const parsed = JSON.parse(event.newValue);
            if (Array.isArray(parsed)) {
              setRecentFeeds(parsed.filter((x): x is string => typeof x === "string"));
            }
          } catch {
            // ignore malformed data
          }
        }
      };
      window.addEventListener("storage", handleStorage);
      return () => window.removeEventListener("storage", handleStorage);
    }
  }, []);

  // When the user is signed in, load their server-side recent feeds and merge
  // with any local history that existed before sign-in on this device.
  useEffect(() => {
    if (!isSignedIn || initializedRef.current) return;
    initializedRef.current = true;

    // Capture local history at the moment we detect sign-in
    localSnapshotRef.current = readRecentFeeds();

    authFetch("/api/users/recent-feeds")
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: { recentFeeds?: string[] }) => {
        if (Array.isArray(data.recentFeeds)) {
          const serverItems = data.recentFeeds as string[];
          const localSnapshot = localSnapshotRef.current;

          // Combine pre-signin local history with any optimistic adds that
          // occurred while the GET was in flight (both are in recentFeedsRef).
          // recentFeedsRef.current comes first so the most recently added feeds
          // (in-flight additions) take precedence over older snapshot entries.
          const allLocalCandidates = [
            ...new Set([...recentFeedsRef.current, ...localSnapshot]),
          ];

          // Merge: server items come first (server order takes priority),
          // then any local-only items not already represented on the server.
          const localOnlyItems = allLocalCandidates.filter(
            (u) => !serverItems.includes(u),
          );
          const merged = [...serverItems, ...localOnlyItems].slice(0, MAX_FEEDS);

          setRecentFeeds(merged);
          writeToStorage(merged);
          channelRef.current?.postMessage(merged);

          // If local history added URLs the server didn't have, persist the
          // merged list back so all devices see the same history going forward.
          if (localOnlyItems.length > 0) {
            authFetch("/api/users/recent-feeds", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ urls: merged }),
            }).catch(() => {
              // non-fatal — localStorage already updated
            });
          }
        }
      })
      .catch(() => {
        // fall back to localStorage already in state
      });
  }, [isSignedIn, authFetch]);

  // Poll the server every 30 s while signed in so changes made on another
  // device are reflected without requiring a manual page refresh.
  useEffect(() => {
    if (!isSignedIn) return;

    const poll = () => {
      // Skip if initialization hasn't completed yet (avoids a race on startup).
      if (!initializedRef.current) return;

      authFetch("/api/users/recent-feeds")
        .then((res) => {
          if (!res.ok) return;
          return res.json();
        })
        .then((data?: { recentFeeds?: string[] }) => {
          if (!data || !Array.isArray(data.recentFeeds)) return;
          const fetched = data.recentFeeds as string[];
          const current = recentFeedsRef.current;
          // Only update if the list has actually changed to avoid re-renders.
          const changed =
            fetched.length !== current.length ||
            fetched.some((u, i) => u !== current[i]);
          if (changed) {
            setRecentFeeds(fetched);
            writeToStorage(fetched);
            channelRef.current?.postMessage(fetched);
          }
        })
        .catch(() => {
          // non-fatal — ignore transient network errors
        });
    };

    // Fire once immediately so a freshly-opened session syncs right away
    // rather than waiting for the first interval tick.
    poll();
    const timerId = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(timerId);
  }, [isSignedIn, authFetch]);

  // Reset initialization flag when user signs out so next sign-in re-fetches
  useEffect(() => {
    if (!isSignedIn) {
      initializedRef.current = false;
      const local = readRecentFeeds();
      setRecentFeeds(local);
    }
  }, [isSignedIn]);

  const addRecentFeed = useCallback(
    (url: string) => {
      const trimmed = url.trim();
      if (!trimmed) return;

      setRecentFeeds((prev) => {
        const without = prev.filter((u) => u !== trimmed);
        const next = [trimmed, ...without].slice(0, MAX_FEEDS);
        writeToStorage(next);
        channelRef.current?.postMessage(next);
        return next;
      });

      if (isSignedIn) {
        authFetch("/api/users/recent-feeds", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url: trimmed }),
        }).catch(() => {
          // non-fatal — localStorage already updated
        });
      }
    },
    [isSignedIn, authFetch],
  );

  const removeRecentFeed = useCallback(
    (url: string) => {
      setRecentFeeds((prev) => {
        const next = prev.filter((u) => u !== url);
        writeToStorage(next);
        channelRef.current?.postMessage(next);
        return next;
      });

      if (isSignedIn) {
        authFetch("/api/users/recent-feeds", {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url }),
        }).catch(() => {
          // non-fatal — localStorage already updated
        });
      }
    },
    [isSignedIn, authFetch],
  );

  return { recentFeeds, addRecentFeed, removeRecentFeed };
}
